import type { ValidatedTool } from '@seen/core/models/validatedTool'
import type { JSX } from 'react'
import type {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
} from 'react-hook-form'
import { getAnswerOptions } from '#src/components/core/combobox/util.ts'
import { ValidatedToolDropdownField } from '#src/components/validated-tool-form/shared/ValidatedToolDropdownField.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import type { ValidatedToolFieldSuggestion } from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'

export interface ValidatedToolFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  validatedTool: ValidatedTool
  className?: string
  /* Optional AI suggestion for THIS field, forwarded to whichever control the
   * instrument declares. Absent ⇒ the field renders exactly as before. */
  suggestion?: ValidatedToolFieldSuggestion
}

export function ValidatedToolField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  validatedTool,
  className,
  suggestion,
}: ValidatedToolFieldProps<TFieldValues, TName>): JSX.Element {
  const options = getAnswerOptions(validatedTool.getItem(name)).map(
    (option) => ({
      value: option.value as FieldPathValue<TFieldValues, TName>,
      label: option.label,
    })
  )
  const itemControl = validatedTool.getItemControl(name)

  return itemControl === 'dropdown' ? (
    <ValidatedToolDropdownField
      control={control}
      name={name}
      options={options}
      label={validatedTool.getItemText(name)}
      className={className}
      suggestion={suggestion}
    />
  ) : (
    <ValidatedToolRadioGroupField
      control={control}
      name={name}
      options={options}
      className={className}
      suggestion={suggestion}
    />
  )
}
